import React, { useEffect, useState } from "react";
import { Table, Card, Row, Col, DatePicker, Typography, Button } from "antd";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";

import PageTitle from "@/components/PageTitle";
import { getStaffRevenueYearly, staffRevenueYearlyExportExcel } from "@/redux/actions/statisticsActions";
import qs from "qs";
import { ExportOutlined, ReloadOutlined } from "@ant-design/icons";
import { NotificationManager } from "react-notifications";
import { checkPermission } from "@/utils/permission";

const { Title } = Typography;

const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/staff-revenue-yearly', breadcrumbName: 'Doanh số nhân viên (năm)' }
];

export default function StaffRevenueYearly() {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const query = qs.parse(location.search.slice(1));

    const allowExport = checkPermission('statistics', 'staff_revenue_yearly_export');

    const currentYear = new Date().getFullYear();
    const defaultYear = query.year ? parseInt(query.year, 10) : currentYear;

    const [year, setYear] = useState(defaultYear);
    const [loading, setLoading] = useState(false);
    const [isLoadingExport, setIsLoadingExport] = useState(false);

    const auth = useSelector((state) => state.auth.authUser);
    const yearlyData = useSelector(
        (state) => state.statisticsReducer.staff_revenue_yearly
    );

    const updateQuery = (newYear) => {
        const q = { ...query, year: newYear };

        history.push({
            pathname: location.pathname,
            search: qs.stringify(q),
        });
    };

    const loadData = async (selectedYear) => {
        setLoading(true);
        await dispatch(getStaffRevenueYearly({ year: selectedYear }));
        setLoading(false);
    };

    useEffect(() => {
        updateQuery(year);
        loadData(year);
    }, [year]);


    const startExcelExport = () => {
        setIsLoadingExport(true);
        dispatch(staffRevenueYearlyExportExcel(query)).catch(() => {
            NotificationManager.error(
                "Có lỗi xảy ra trong quá trình gửi yêu cầu xuất file. Vui lòng thử lại sau.",
                "Lỗi",
                9000
            );
        }).finally(() => {
            setIsLoadingExport(false);
        });

        setIsLoadingExport(false);
        // Thông báo ngay lập tức cho người dùng
        NotificationManager.success(
            `Quá trình xuất file đang được thực hiện. 
            File kết quả sẽ được gửi vào email ${auth.user_email} trong ít phút.`,
            "Thông báo",
            9000
        );
    }

    const columns = [
        {
            title: "Nhân viên",
            dataIndex: "staff_name",
            fixed: "left",
            width: 190,
            render: (value) => <strong>{value}</strong>
        },
        ...Array.from({ length: 12 }, (_, i) => ({
            title: `Tháng ${i + 1}`,
            dataIndex: ["months", `${i + 1}`],
            width: 110,
            align: "right",
            render: (value) => (value ? value.toLocaleString("vi-VN") : "0")
        })),
        {
            title: <strong style={{ color: "#2c3e50" }}>Tổng năm</strong>,
            dataIndex: "total_year",
            width: 120,
            align: "right",
            fixed: "right",
            render: (value) => (
                <strong style={{ color: "#2c3e50" }}>
                    {value.toLocaleString("vi-VN")}
                </strong>
            )
        },
        {
            title: <strong style={{ color: "#1890ff" }}>TB/Tháng</strong>,
            dataIndex: "avg_month",
            width: 110,
            align: "right",
            fixed: "right",
            render: (v) => (
                <strong style={{ color: "#1890ff" }}>
                    {v.toLocaleString("vi-VN")}
                </strong>
            )
        },
        {
            title: <strong style={{ color: "#13c2c2" }}>TB/3T gần nhất</strong>,
            dataIndex: "avg_3m",
            width: 130,
            align: "right",
            fixed: "right",
            render: (v) => (
                <strong style={{ color: "#13c2c2" }}>
                    {v.toLocaleString("vi-VN")}
                </strong>
            )
        }

    ];

    return (
        <div>
            <PageTitle routes={routes} title="Tổng quan theo năm" />

            <div className="mt-2">
                <Title level={3}>Thống kê doanh số nhân viên theo năm</Title>

                <Row gutter={16} style={{ marginBottom: 10 }}>
                    <Col xs={24} md={24}>
                        <DatePicker
                            picker="year"
                            format="YYYY"
                            className="table-button"
                            value={moment(year, "YYYY")}
                            onChange={(v) =>
                                setYear(v ? v.year() : currentYear)
                            }
                        />
                        {allowExport && (
                            <Button
                                className="table-button"
                                loading={isLoadingExport}
                                type="default"
                                onClick={startExcelExport}
                                icon={<ExportOutlined />}
                            >
                                Export
                            </Button>
                        )}
                        <Button className="table-button" icon={<ReloadOutlined />} loading={loading} onClick={() => loadData(year)}>Refresh</Button>
                    </Col>
                </Row>

                <Table
                    size="small"
                    columns={columns}
                    dataSource={yearlyData}
                    loading={loading}
                    rowKey="staff_id"
                    bordered
                    scroll={{ x: 1800 }}
                    pagination={false}
                />
            </div>
        </div>
    );
}
